import { AnyObject } from "../../../global";
import FieldRenderType from "./enums/FieldRenderType";
import Validation from "../Validation";
import { MAX_DEFAULT_EMAIL_LENGTH, MAX_DEFAULT_URL_LENGTH } from "../../utils/constants";
import { ValidationDecision } from "./types/Decision";
import OperatorType from "./enums/OperatorType";
import { SchemaField } from "./types/SchemaField";
import { Copies } from "shared/hooks/useCopies";
import FieldAlias from "./enums/FieldAlias";
import { ORGANIZATION_LOGO_ASSET_CONFIG } from "../core/assetUpload/constants";

/**
 * @interface ValidationSchemaType
 */
interface ValidationSchemaType {
  schemaObj: SchemaField;
  schemaList: Array<SchemaField>;
  fieldValue: any;
  formValues: AnyObject;
  copies: Copies;
}

/**
 * @class ValidationEvaluator Contains methods to evaluate validations for each field type
 */
class ValidationEvaluator {
  /**
   * Will get validation for fields either it is static validation or dynamic validation
   * @param props
   */
  static getValidationForSchema(props: ValidationSchemaType) {
    const fieldStaticValidation: any = this.getStaticSchemaFieldValidation(props);
    if (!fieldStaticValidation) {
      return;
    }
    const fieldDynamicValidation = this.getDynamicSchemaFieldValidation({
      schemaObj: props.schemaObj,
      formValues: props.formValues,
      schemaList: props.schemaList
    });
    if (!fieldDynamicValidation) {
      return fieldStaticValidation
        .validate(props.fieldValue)
        .then(() => undefined)
        .catch(({ errors }: { errors: Array<any> }) => (errors ? errors[0] : ""));
    }
    return fieldStaticValidation
      .test("dynamicValidation", fieldDynamicValidation.validationMessage, fieldDynamicValidation.evaluatorFunction)
      .validate(props.fieldValue)
      .then(() => undefined)
      .catch(({ errors }: { errors: Array<any> }) => (errors ? errors[0] : ""));
  }

  /**
   * Used to evaluate dynamic validation for field
   * @param props
   * @private
   */
  private static getDynamicSchemaFieldValidation(props: {
    schemaObj: SchemaField;
    formValues: AnyObject;
    schemaList: Array<SchemaField>;
  }) {
    const validationDecisionObj = props.schemaObj.validationDecision;
    if (!validationDecisionObj) {
      return null;
    }
    return {
      validationMessage: (value: any) => {
        const evaluatedVal =
          value &&
          this.evaluateValidationDecision({
            validationDecisionObj,
            formValues: props.formValues,
            schemaList: props.schemaList,
            fieldValue: value?.originalValue
          });
        return evaluatedVal && (evaluatedVal.validationMessage || "invalid value");
      },
      evaluatorFunction: (value: any) => {
        const evaluatedVal =
          value &&
          this.evaluateValidationDecision({
            validationDecisionObj,
            formValues: props.formValues,
            schemaList: props.schemaList,
            fieldValue: value
          });
        return !evaluatedVal;
      }
    };
  }

  /**
   * Evaluating decision based on operator for dynamic validation decision map
   * @param props
   * @private
   * @return ValidationDecision | null
   */
  private static evaluateValidationDecision(props: {
    validationDecisionObj: ValidationDecision;
    formValues: AnyObject;
    schemaList: Array<SchemaField>;
    fieldValue: any;
  }): any {
    const targetFieldSchemaObj = props.schemaList.find(item => item.id === props.validationDecisionObj.key);
    if (!targetFieldSchemaObj && !props.validationDecisionObj.operands) {
      return null;
    }
    switch (props.validationDecisionObj.operator) {
      case OperatorType.NOT_EQUAL:
        if (
          targetFieldSchemaObj &&
          (props.formValues[targetFieldSchemaObj.id] || "").trim().toLowerCase() !==
            (props.fieldValue || "").trim().toLowerCase()
        ) {
          return props.validationDecisionObj;
        }
        return null;
      case OperatorType.EQUAL:
        if (
          targetFieldSchemaObj &&
          (props.formValues[targetFieldSchemaObj.id] || "").trim().toLowerCase() ===
            (props.fieldValue || "").trim().toLowerCase()
        ) {
          return props.validationDecisionObj;
        }
        return null;
      case OperatorType.AND:
        return props.validationDecisionObj.operands.find(item => {
          return this.evaluateValidationDecision({
            validationDecisionObj: item,
            formValues: props.formValues,
            schemaList: props.schemaList,
            fieldValue: props.fieldValue
          });
        });
      case OperatorType.OR:
        return props.validationDecisionObj.operands.find(item => {
          return !this.evaluateValidationDecision({
            validationDecisionObj: item,
            formValues: props.formValues,
            schemaList: props.schemaList,
            fieldValue: props.fieldValue
          });
        });
      default:
        return true;
    }
  }

  /**
   * To evaluate static validations
   * @param props
   * @private
   */
  private static getStaticSchemaFieldValidation(props: ValidationSchemaType) {
    switch (props.schemaObj.renderType) {
      case FieldRenderType.CHECK_BOX:
        return Validation.yupIsReqCheckbox({ copies: props.copies, required: props.schemaObj.mandatory });
      case FieldRenderType.PHONE:
        return Validation.phoneNumberFieldValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory
        });
      case FieldRenderType.SINGLE_LINE_TEXTFIELD:
      case FieldRenderType.ORGANIZATION_NAME:
        return Validation.textFieldValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum,
          regex: props.schemaObj.validationRegex,
          regexMsg: props.schemaObj.validationRegexMessage
        });
      case FieldRenderType.MULTI_LINE_TEXTFIELD:
        return Validation.textAreaValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum,
          regex: props.schemaObj.validationRegex,
          regexMsg: props.schemaObj.validationRegexMessage
        });
      case FieldRenderType.MULTI_LINE_TEXTFIELD_HTML_INPUT:
        return Validation.textAreaValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum,
          minCharactersCopyKey: "min.characters.including.html",
          maxCharactersCopyKey: "max.characters.including.html"
        });
      case FieldRenderType.MULTI_SELECT:
      case FieldRenderType.DIVISION:
      case FieldRenderType.NESTED_MULTI_SELECT:
        return Validation.multiSelectValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum
        });
      case FieldRenderType.TAGS:
        return Validation.multiSelectValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum,
          minCopyKey: "add.minimum.tag.validation",
          maxCopyKey: "add.maximum.tag.validation"
        });
      case FieldRenderType.MULTI_IMAGE:
        return Validation.multiSelectValidation({
          copies: props.copies,
          copyKey: "required",
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum,
          minCopyKey: "upload.minimum.images.validation",
          maxCopyKey: "upload.maximum.images.validation"
        });
      case FieldRenderType.MULTI_FILE:
      case FieldRenderType.PRIVATE_MULTI_FILE:
        return Validation.multiSelectValidation({
          copies: props.copies,
          copyKey: "required",
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum,
          minCopyKey: "upload.minimum.files.validation",
          maxCopyKey: "upload.maximum.files.validation"
        });
      case FieldRenderType.RADIO:
      case FieldRenderType.SINGLE_SELECT:
      case FieldRenderType.DATE:
      case FieldRenderType.DATE_TIME:
        return Validation.yupIsReq({
          copies: props.copies,
          required: props.schemaObj.mandatory
        });
      case FieldRenderType.SINGLE_IMAGE:
      case FieldRenderType.SINGLE_IMAGE_WITH_ASPECT_RATIO:
        if (props.schemaObj.alias === FieldAlias.ORGANIZATION_LOGO) {
          return Validation.assetUploadValidation({
            copies: props.copies,
            required: props.schemaObj.mandatory,
            assetConfig: ORGANIZATION_LOGO_ASSET_CONFIG
          });
        } else
          return Validation.yupIsReq({
            copies: props.copies,
            required: props.schemaObj.mandatory
          });
      case FieldRenderType.EMAIL:
        return Validation.emailValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum || MAX_DEFAULT_EMAIL_LENGTH,
          regex: props.schemaObj.validationRegex,
          regexMsg: props.schemaObj.validationRegexMessage
        });
      case FieldRenderType.EMAIL_LIST:
        return Validation.emailValidationArray({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum
        });
      case FieldRenderType.URL:
      case FieldRenderType.URL_WITH_ICON:
        return Validation.urlValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          min: props.schemaObj.minimum,
          max: props.schemaObj.maximum || MAX_DEFAULT_URL_LENGTH
        });
      case FieldRenderType.MONEY:
        return Validation.numberValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          max: props.schemaObj.maximum,
          min: props.schemaObj.minimum
        });
      case FieldRenderType.INTEGER:
        return Validation.integerValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          max: props.schemaObj.maximum,
          min: props.schemaObj.minimum
        });
      case FieldRenderType.IMAGE:
        return Validation.imageValidation({
          copies: props.copies,
          required: props.schemaObj.mandatory,
          max: props.schemaObj.maximum
        });
      default:
        return null;
    }
  }
}

export default ValidationEvaluator;
